<template>
  <div ref="root" class="lg-select" @keydown="onKeydown">
    <button
      :id="triggerId"
      ref="trigger"
      type="button"
      class="lg-select-trigger"
      :class="{ open }"
      :disabled="disabled"
      role="combobox"
      aria-haspopup="listbox"
      :aria-expanded="open"
      :aria-controls="listId"
      :aria-label="ariaLabel"
      @click="toggle"
    >
      <span class="lg-select-value" :class="{ placeholder: selectedLabel === null }">
        {{ selectedLabel ?? placeholder }}
      </span>
      <ChevronDown class="lg-select-caret" :size="16" aria-hidden="true" />
    </button>

    <ul
      v-if="open"
      :id="listId"
      ref="list"
      class="lg-select-list"
      role="listbox"
      :aria-activedescendant="activeId"
      tabindex="-1"
    >
      <li
        v-for="(opt, i) in normalized"
        :id="`${listId}-o${i}`"
        :key="opt.value"
        class="lg-select-option"
        :class="{ active: i === activeIndex, selected: opt.value === modelValue }"
        role="option"
        :aria-selected="opt.value === modelValue"
        @click="choose(i)"
        @mousemove="activeIndex = i"
      >
        {{ opt.label }}
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount, nextTick } from 'vue';
import { ChevronDown } from 'lucide-vue-next';

type Option = { value: string; label: string };

const props = withDefaults(
  defineProps<{
    modelValue: string;
    options: Array<Option | string>;
    placeholder?: string;
    disabled?: boolean;
    ariaLabel?: string;
  }>(),
  { placeholder: 'Select…', disabled: false },
);

const emit = defineEmits<{ 'update:modelValue': [value: string] }>();

let idSeed = 0;
const uid = `lgsel-${(idSeed = Math.floor(Math.random() * 1e6))}`;
const triggerId = `${uid}-t`;
const listId = `${uid}-l`;

const normalized = computed<Option[]>(() =>
  props.options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o)),
);

const selectedLabel = computed<string | null>(
  () => normalized.value.find((o) => o.value === props.modelValue)?.label ?? null,
);

const open = ref(false);
const activeIndex = ref(0);
const root = ref<HTMLElement | null>(null);
const trigger = ref<HTMLButtonElement | null>(null);
const list = ref<HTMLElement | null>(null);

const activeId = computed(() => (open.value ? `${listId}-o${activeIndex.value}` : undefined));

function openList() {
  if (props.disabled) return;
  const cur = normalized.value.findIndex((o) => o.value === props.modelValue);
  activeIndex.value = cur >= 0 ? cur : 0;
  open.value = true;
  nextTick(() => list.value?.focus());
}
function close(refocus = true) {
  open.value = false;
  if (refocus) nextTick(() => trigger.value?.focus());
}
function toggle() {
  open.value ? close() : openList();
}
function choose(i: number) {
  const opt = normalized.value[i];
  if (opt) emit('update:modelValue', opt.value);
  close();
}
function move(delta: number) {
  const n = normalized.value.length;
  if (!n) return;
  activeIndex.value = (activeIndex.value + delta + n) % n;
}

function onKeydown(e: KeyboardEvent) {
  if (!open.value) {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
      e.preventDefault();
      openList();
    }
    return;
  }
  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault();
      move(1);
      break;
    case 'ArrowUp':
      e.preventDefault();
      move(-1);
      break;
    case 'Home':
      e.preventDefault();
      activeIndex.value = 0;
      break;
    case 'End':
      e.preventDefault();
      activeIndex.value = normalized.value.length - 1;
      break;
    case 'Enter':
    case ' ':
      e.preventDefault();
      choose(activeIndex.value);
      break;
    case 'Escape':
      e.preventDefault();
      close();
      break;
    case 'Tab':
      close(false);
      break;
  }
}

function onDocPointer(e: MouseEvent) {
  if (open.value && root.value && !root.value.contains(e.target as Node)) close(false);
}
document.addEventListener('pointerdown', onDocPointer);
onBeforeUnmount(() => document.removeEventListener('pointerdown', onDocPointer));
</script>

<style scoped>
.lg-select {
  position: relative;
  display: inline-block;
}
.lg-select-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--lg-space-2);
  width: 100%;
  min-height: var(--lg-control-md);
  padding: 0 var(--lg-space-2) 0 var(--lg-space-3);
  font-family: var(--lg-font-sans);
  font-size: var(--lg-fs-md);
  border-radius: var(--lg-radius-md);
  border: 1px solid var(--lg-border-strong);
  background: var(--lg-bg-surface);
  color: var(--lg-text);
  cursor: pointer;
}
.lg-select-trigger:disabled {
  opacity: 0.55;
  cursor: default;
}
.lg-select-trigger:hover:not(:disabled) {
  border-color: var(--lg-accent);
}
.lg-select-trigger:focus-visible {
  outline: none;
  box-shadow: var(--lg-focus);
}
.lg-select-value.placeholder {
  color: var(--lg-text-faint);
}
.lg-select-caret {
  flex-shrink: 0;
  color: var(--lg-text-dim);
}

.lg-select-list {
  position: absolute;
  z-index: var(--lg-z-menu);
  top: calc(100% + var(--lg-space-1));
  left: 0;
  min-width: 100%;
  max-height: 260px;
  overflow-y: auto;
  margin: 0;
  padding: var(--lg-space-1);
  list-style: none;
  border-radius: var(--lg-radius-md);
  border: 1px solid var(--lg-border);
  background: var(--lg-bg-raised);
  box-shadow: var(--lg-shadow-2);
}
.lg-select-list:focus {
  outline: none;
}
.lg-select-option {
  padding: var(--lg-space-2) var(--lg-space-3);
  border-radius: var(--lg-radius-sm);
  font-size: var(--lg-fs-md);
  color: var(--lg-text);
  cursor: pointer;
  white-space: nowrap;
}
.lg-select-option.active {
  background: var(--lg-bg-hover);
}
.lg-select-option.selected {
  color: var(--lg-accent);
}
</style>
